import { Asset } from 'expo-asset';
import { CodedError } from 'expo-modules-core';
import ExpoFontLoader from './ExpoFontLoader';
import { normalizeWeight, resolveFaceStyle, resolveFaceWeight } from './fontFaceValidation';
import { fontSourceFromFace } from './fontSourceFromFace';
function uriFromFontSource(asset) {
    if (typeof asset === 'string') {
        return asset || null;
    } else if (typeof asset === 'number') {
        return uriFromFontSource(Asset.fromModule(asset));
    } else if (typeof asset === 'object' && typeof asset.uri === 'number') {
        return uriFromFontSource(asset.uri);
    } else if (typeof asset === 'object') {
        return asset.uri || asset.localUri || asset.default || null;
    }
    return null;
}
function displayFromFontSource(asset) {
    if (typeof asset === 'object' && 'display' in asset) {
        return asset.display ?? undefined;
    }
    return undefined;
}
function weightFromFontSource(asset) {
    if (typeof asset === 'object' && 'weight' in asset) {
        return asset.weight ?? undefined;
    }
    return undefined;
}
function styleFromFontSource(asset) {
    if (typeof asset === 'object' && 'style' in asset) {
        return asset.style ?? undefined;
    }
    return undefined;
}
export function getAssetForSource(source) {
    const uri = uriFromFontSource(source);
    const display = displayFromFontSource(source);
    const weight = weightFromFontSource(source);
    const style = styleFromFontSource(source);
    if (!uri || typeof uri !== 'string') {
        throwInvalidSourceError(uri);
    }
    return {
        uri,
        display,
        weight,
        style
    };
}
function throwInvalidSourceError(source) {
    let type = typeof source;
    if (type === 'object') type = JSON.stringify(source, null, 2);
    throw new CodedError(`ERR_FONT_SOURCE`, `Expected font asset of type \`string | FontResource | Asset\` instead got: ${type}`);
}
// The shared declared-value check skips faces with an undeclared weight or style. On web an
// undeclared descriptor is not read from the font file — CSS defaults it to 'normal'/400 — so
// two faces can resolve to the same effective descriptors and the last one registered silently
// shadows the rest. Warn about that; ranges only collide when they are identical.
function warnOnCollidingFaces(fontFamily, fontDefinitions) {
    const seenFaces = new Set();
    for (const face of fontDefinitions){
        const weight = resolveFaceWeight(face);
        const weightKey = normalizeWeight(weight) ?? (typeof weight === 'string' ? weight.trim().toLowerCase() : 400);
        const styleKey = (resolveFaceStyle(face) ?? 'normal').trim().toLowerCase();
        const key = `${weightKey}/${styleKey}`;
        if (seenFaces.has(key)) {
            console.warn(`Font family "${fontFamily}" declares two faces that both resolve to font-weight ` + `${weightKey} and font-style "${styleKey}" on web. The browser renders the last one ` + `registered and silently ignores the rest. Give each face a distinct weight or style.`);
            return;
        }
        seenFaces.add(key);
    }
}
export async function loadFontFamilyAsync(fontFamily, fontDefinitions) {
    if (__DEV__) {
        warnOnCollidingFaces(fontFamily, fontDefinitions);
    }
    await Promise.all(fontDefinitions.map((face)=>{
        const asset = getAssetForSource(fontSourceFromFace(face));
        return loadSingleFontAsync(fontFamily, asset);
    }));
}
// NOTE(EvanBacon): No async keyword!
export function loadSingleFontAsync(name, input) {
    if (typeof input !== 'object' || typeof input.uri !== 'string' || input.downloadAsync) {
        throwInvalidSourceError(input);
    }
    return ExpoFontLoader.loadAsync(name, input);
}

//# sourceMappingURL=FontLoader.web.js.map
